import { useQuery } from '@tanstack/react-query';
import searchClient from '@/lib/algolia/searchClient';
import { index } from '@/lib/algolia/client.env';
import { fetchPaginatedArticles } from '@/sanity/services/articleService';

const fetchAlgoliaData = async (
  page: number,
  query: string,
  searchFilters: any
) => {
  const maxHitsPerPage = 20;

  const buildFilters = () => {
    const filters = [];
    if (searchFilters?.category) {
      filters.push([`category.slug:${searchFilters.category}`]);
    }
    if (searchFilters?.tag) {
      filters.push([`tags.slug:${searchFilters.tag}`]);
    }
    return filters;
  };

  const filters = buildFilters();

  const { results }: any = await searchClient.search({
    requests: [
      {
        indexName: index,
        query: query,
        hitsPerPage: maxHitsPerPage,
        page: page - 1,
        attributesToHighlight: ['title', 'seoDescription'],
        facetFilters: filters,
        facets: ['*'],
      },
    ],
  });

  const [resultObject] = results;

  const articles = resultObject.hits.map((article: any) => ({
    ...article,
    image: {
      url: article.image,
    },
    excerpt: article.seoDescription,
  }));

  return {
    articles,
    totalHits: resultObject.nbHits,
    totalPages: resultObject.nbPages,
    currentPage: resultObject.page + 1,
  };
};

const fetchSanityData = async (
  page: number,
  limit: number = 20,
  filter?: string
) => {
  const { articles, total } = await fetchPaginatedArticles(page, limit, filter);

  return {
    articles,
    totalHits: total,
    totalPages: Math.ceil(total / limit),
    currentPage: page,
  };
};

// unified fetcher for both SSR and client
export const fetchBlogData = async (
  page: number,
  query: string,
  searchFilters: any,
  useAlgolia: boolean
) => {
  return useAlgolia
    ? fetchAlgoliaData(page, query, searchFilters)
    : fetchSanityData(page, 20, searchFilters?.filter || '');
};

type BlogDataResult = {
  articles: Array<{
    [key: string]: any;
    image: { url: string };
    excerpt: string;
  }>;
  totalHits: number;
  totalPages: number;
  currentPage: number;
};

export const useUnifiedBlogData = (
  page: number,
  query: string,
  searchFilters: any,
  useAlgolia: boolean
) => {
  return useQuery<BlogDataResult>({
    queryKey: [
      'blogData',
      page,
      query || '_blank',
      searchFilters?.category || '_blank',
      searchFilters.tag || '_blank',
      useAlgolia,
    ],
    queryFn: () => fetchBlogData(page, query, searchFilters, useAlgolia),
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes (was cacheTime)
    refetchOnWindowFocus: false,
    retry: 2,
    retryDelay: 1000,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });
};
